// Per-lift cost and breakdown analytics (/office/lift-performance, Session ANO). The single most
// commercially important item named open in
// docs/ELE-V2-GAP-ANALYSIS-RECONCILED-2026-09-06.md section 1: the platform could answer "did this
// visit make money" per JOB (the Business analytics profitability panel) and could not answer "is
// this lift worth maintaining at this price", which is the question a renewal actually turns on.
//
// Every figure here is a SUM or a COUNT over data another surface already owns and already tests:
// profitability.ts's per-job cost/charge/margin (summed by lift), a count of call-outs (never a PPM
// visit) raised against the lift, and the repeat-visit signal, which is slaStats.ts's OWN
// first-time-fix recurrence definition reused unchanged -- never a second "repeat" that could
// quietly disagree with the Call-out response performance report. Nothing here is recomputed; the
// server aggregate (GET /api/reporting/lift-performance) is the only arithmetic.
//
// MTBF is deliberately NOT shown (see liftPerformance.ts's header for the full reasoning): at ELE's
// live scale a mean-time-between-failures figure would rest on one or two data points per lift while
// looking precise. Breakdown COUNT over the selected window is shown instead, honestly, with the
// window it counts over stated alongside it.
import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { LiftPerformancePanel, LiftPerformanceRow } from "../../lib/api";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { EmptyState } from "../../components/EmptyState";
import { useSortableColumn, sortRows, type SortAccessors } from "../../lib/useSortableColumn";
import { SortableHeader } from "../../components/SortableHeader";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { TapReconciliationSection } from "./TapReconciliationSection";

function gbpPence(p: number): string {
  return `£${(p / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// The default window: the current calendar month, matching every other reporting panel
// (reporting.ts's parseReportingWindow) so a lift's figures here read consistently with the
// Business analytics dashboard's own profitability panel for the same period.
function currentMonthWindow(now: Date): { from: string; to: string } {
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0));
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { from: fmt(start), to: fmt(end) };
}

const LIFT_SORT: SortAccessors<LiftPerformanceRow> = {
  lift: (r) => r.ele,
  site: (r) => r.siteName,
  jobs: (r) => r.jobCount,
  cost: (r) => r.totalCostPence ?? null,
  charge: (r) => r.totalChargePence,
  margin: (r) => r.marginPence ?? null,
  breakdown: (r) => r.breakdownCount,
  repeat: (r) => r.repeatVisitCount,
};

function marginCell(r: LiftPerformanceRow): { text: string; className: string } {
  if (r.marginPence == null) return { text: "Not recorded", className: "text-ele-body opacity-60" };
  const pct = r.marginPercent != null ? ` (${r.marginPercent}%)` : "";
  return {
    text: `${gbpPence(r.marginPence)}${pct}`,
    className: r.marginPence < 0 ? "text-ele-danger" : "text-ele-green",
  };
}

export function LiftPerformancePage() {
  const initial = currentMonthWindow(new Date());
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [applied, setApplied] = useState(initial);
  const [data, setData] = useState<LiftPerformancePanel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const sort = useSortableColumn("lift-performance");
  const [mobileCards] = useState(initialMobileLayout);

  useEffect(() => {
    let active = true;
    setError(null);
    setData(null);
    api.reporting
      .liftPerformance(applied.from || undefined, applied.to || undefined)
      .then((d) => {
        if (active) setData(d);
      })
      .catch(() => {
        if (active) setError("The per-lift performance report could not be loaded. Please try again.");
      });
    return () => {
      active = false;
    };
  }, [applied, reloadKey]);

  const applyWindow = useCallback(() => setApplied({ from, to }), [from, to]);
  const retry = useCallback(() => setReloadKey((k) => k + 1), []);

  const rows = data ? sortRows(data.rows, LIFT_SORT, sort) : [];
  // Session BDL (F3): cost and margin are built from engineers' cost rates (admin only), so the server leaves them out for anyone
  // else. When no row carries them, those two columns are not drawn at all.
  const costVisible = rows.some((r) => r.marginPence !== undefined);

  return (
    <div>
      <PageHeader
        className="mb-5"
        title="Lift performance"
        subtitle="Cost, charge and margin per lift, plus how often each one has broken down and how often a call-out came back round. Sort by margin to find the lifts costing more than they earn; sort by breakdowns to find the ones failing most often."
        actions={
          <>
            <label className="block">
              <span className="mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-blue">From</span>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm text-ele-strong"
              />
            </label>
            <label className="block">
              <span className="mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-blue">To</span>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm text-ele-strong"
              />
            </label>
            <Button size="sm" onClick={applyWindow}>
              Apply
            </Button>
          </>
        }
      />

      {error ? (
        <div className="rounded-ele border border-ele-purple-accent bg-ele-surface-soft p-5">
          <p role="alert" className="text-sm text-ele-danger">
            {error}
          </p>
          <button
            onClick={retry}
            className="mt-3 rounded-ele border border-ele-purple-accent px-4 py-1.5 font-heading text-sm tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-strong"
          >
            Try again
          </button>
        </div>
      ) : data === null ? (
        <Loader />
      ) : (
        <section className="rounded-ele border border-ele-purple-accent bg-ele-surface-soft p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading text-xl text-ele-blue">By lift</h2>
            <span className="font-menu text-xs uppercase tracking-wide text-ele-body opacity-70">{data.window.label}</span>
          </div>
          {rows.length === 0 ? (
            <EmptyState compact message="No completed jobs and no call-outs were recorded against any lift in this period." />
          ) : mobileCards ? (
            <MobileCardList label="Lift">
              {rows.map((r) => {
                const margin = marginCell(r);
                return (
                  <MobileListCard
                    key={r.assetId}
                    primary={r.ele}
                    secondary={r.siteName}
                    meta={`${r.jobCount} job${r.jobCount === 1 ? "" : "s"} · ${r.breakdownCount} breakdown${r.breakdownCount === 1 ? "" : "s"} · ${r.repeatVisitCount} repeat${r.repeatVisitCount === 1 ? "" : "s"}`}
                    right={costVisible ? <span className={`font-heading text-base ${margin.className}`}>{margin.text}</span> : undefined}
                  />
                );
              })}
            </MobileCardList>
          ) : (
            <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
              <table className="ele-list-table w-full text-left text-sm">
                <colgroup>
                  {costVisible ? (
                    <>
                      <col style={{ width: "16%" }} />
                      <col style={{ width: "20%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "12%" }} />
                      <col style={{ width: "12%" }} />
                      <col style={{ width: "16%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "8%" }} />
                    </>
                  ) : (
                    <>
                      <col style={{ width: "22%" }} />
                      <col style={{ width: "28%" }} />
                      <col style={{ width: "10%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "13%" }} />
                      <col style={{ width: "13%" }} />
                    </>
                  )}
                </colgroup>
                <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                  <tr>
                    <SortableHeader label="Lift" colKey="lift" sort={sort} onToggle={sort.toggle} />
                    <SortableHeader label="Site" colKey="site" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                    <SortableHeader label="Jobs" colKey="jobs" sort={sort} onToggle={sort.toggle} align="right" />
                    {costVisible ? <SortableHeader label="Cost" colKey="cost" sort={sort} onToggle={sort.toggle} align="right" className="max-md:hidden" /> : null}
                    <SortableHeader label="Charge" colKey="charge" sort={sort} onToggle={sort.toggle} align="right" className="max-md:hidden" />
                    {costVisible ? <SortableHeader label="Margin" colKey="margin" sort={sort} onToggle={sort.toggle} align="right" /> : null}
                    <SortableHeader label="Breakdowns" colKey="breakdown" sort={sort} onToggle={sort.toggle} align="right" />
                    <SortableHeader label="Repeats" colKey="repeat" sort={sort} onToggle={sort.toggle} align="right" className="max-md:hidden" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const margin = marginCell(r);
                    return (
                      <tr key={r.assetId}>
                        <td className="truncate px-3 py-2 text-ele-strong" title={r.ele}>
                          {r.ele}
                        </td>
                        <td className="truncate px-3 py-2 text-ele-body max-md:hidden" title={r.siteName}>
                          {r.siteName}
                        </td>
                        <td className="px-3 py-2 text-right text-ele-body">{r.jobCount}</td>
                        {costVisible ? <td className="px-3 py-2 text-right text-ele-body max-md:hidden">{gbpPence(r.totalCostPence ?? 0)}</td> : null}
                        <td className="px-3 py-2 text-right text-ele-body max-md:hidden">{gbpPence(r.totalChargePence)}</td>
                        {costVisible ? <td className={`px-3 py-2 text-right ${margin.className}`}>{margin.text}</td> : null}
                        <td className="px-3 py-2 text-right text-ele-body">{r.breakdownCount}</td>
                        <td className="px-3 py-2 text-right text-ele-body max-md:hidden">{r.repeatVisitCount}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-3 text-xs text-ele-body opacity-70">
            "Repeats" counts call-outs on that lift not fixed first time (a follow-up was needed, or another call-out
            came back within {data.recurrenceDays} days), the same rule the Call-out response report uses, so the two
            pages can never quietly disagree. {costVisible ? "Cost, charge and margin" : "Charge"} cover completed jobs of every type against the
            lift, not call-outs alone{costVisible ? "; a lift with no charge recorded (a contract-covered visit) shows \"Not recorded\" rather than a misleading zero" : ". Cost and margin are shown to admins only"}.
          </p>
        </section>
      )}
      <TapReconciliationSection from={applied.from} to={applied.to} />
    </div>
  );
}
